import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { fetchReports } from "../redux/actions/reports";
import UsePagination from "../components/custom/UsePagination";
import DownloadReportSkeleton from "./custom/skeletonLoader/DownloadReportSkeleton";

function DownloadReport() {
  const dispatch = useDispatch();
  const {
    items: reports,
    status,
    error,
  } = useSelector((state) => state.reports);

  const [sortedReports, setSortedReports] = useState([]);

  useEffect(() => {
    dispatch(fetchReports());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded") {
      const approvedReports = reports.filter(
        (report) => report.status === "approved"
      );
      const sorted = [...approvedReports].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setSortedReports(sorted);
    }
  }, [reports, status]);

  const itemsPerPage = 12;
  const {
    currentItems,
    totalPages,
    currentPage,
    paginate,
    nextPage,
    prevPage,
  } = UsePagination(sortedReports, itemsPerPage);

  const showPagination = sortedReports.length > itemsPerPage;

  if (status === "loading") {
    return <DownloadReportSkeleton />;
  }

  if (status === "failed" && error) {
    return (
      <div className="w-100 h-33 my-5">
        <div className="bg-white py-5">
          <div className="container">
            <div className="d-flex w-100 rounded border-start border-danger border-5 bg-light p-4">
              <div className="me-3 text-danger">
                <svg
                  width="34"
                  height="34"
                  viewBox="0 0 34 34"
                  className="fill-current"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.9999 2.83366C9.57774 2.83366 3.83325 8.57815 3.83325 16.0003C3.83325 23.4225 9.57774 29.167 16.9999 29.167C24.4221 29.167 30.1666 23.4225 30.1666 16.0003C30.1666 8.57815 24.4221 2.83366 16.9999 2.83366ZM0.333252 16.0003C0.333252 7.54021 7.53979 0.33366 15.9999 0.33366C24.46 0.33366 31.6666 7.54021 31.6666 16.0003C31.6666 24.4604 24.46 31.667 15.9999 31.667C7.53979 31.667 0.333252 24.4604 0.333252 16.0003ZM17.8333 8.83366C17.8333 8.18138 17.3182 7.66699 16.6666 7.66699C16.0143 7.66699 15.4999 8.18138 15.4999 8.83366V18.0846C15.4999 18.7369 16.0143 19.2513 16.6666 19.2513C17.3182 19.2513 17.8333 18.7369 17.8333 18.0846V8.83366ZM16.6666 24.5003C17.3182 24.5003 17.8333 23.9859 17.8333 23.3337C17.8333 22.6814 17.3182 22.167 16.6666 22.167C16.0143 22.167 15.4999 22.6814 15.4999 23.3337C15.4999 23.9859 16.0143 24.5003 16.6666 24.5003Z"
                  ></path>
                </svg>
              </div>
              <div className="w-100 mb-3">
                <div className="mb-2 d-flex align-items-center justify-content-between">
                  <h5 className="fs-5 fw-medium text-danger">
                    {error.message}
                  </h5>
                </div>
                <p className="text-body">Please try again later.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (sortedReports.length === 0) {
    return (
      <div className="text-center mb-5 mt-5">
        <h1>No approved reports available</h1>
      </div>
    );
  }

  return (
    <section className="download-report pt_120 pb_90">
      <div className="auto-container">
        <div className="sec-title centred mb_70">
          <h6>Financial Reports</h6>
          <h2>Download Financial Statements</h2>
        </div>
        <div className="inner-container">
          <div className="row">
            {currentItems.map((item) => (
              <div key={item._id} className="col-lg-4 col-md-6 col-sm-12">
                <div className="download-content">
                  <div className="inner-box">
                    <h3>{item.title}</h3>
                    <div className="report-btn">
                      <a
                        href={item.downloadFile}
                        className="download-btn btn-link"
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faDownload} />{" "}
                        <span>Download</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {showPagination && (
            <div className="pagination-wrapper centred pt_50">
              <ul className="pagination">
                <li>
                  <Link
                    to="#"
                    onClick={prevPage}
                    className={`page-link ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <i className="icon-40"></i>
                  </Link>
                </li>
                {[...Array(totalPages)].map((_, index) => (
                  <li
                    key={index}
                    className={`page-item ${
                      currentPage === index + 1 ? "active" : ""
                    }`}
                  >
                    <Link
                      to="#"
                      className="page-link"
                      onClick={() => paginate(index + 1)}
                    >
                      {index + 1}
                    </Link>
                  </li>
                ))}
                <li className="page-item">
                  <Link
                    to="#"
                    onClick={nextPage}
                    className={`page-link ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <i className="icon-41"></i>
                  </Link>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default DownloadReport;
